/**
 * SettingsModal Component
 * Modal dialog for app customization, wallpapers, quadrant configuration, local backup, and general preferences.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, Palette, Sliders, LayoutGrid, Database } from 'lucide-react';
import { WallpaperPicker } from './WallpaperPicker';
import { QuadrantSettings } from './QuadrantSettings';
import { BackupSettings } from './BackupSettings';
import { GeneralSettings } from './GeneralSettings';

export function SettingsModal({
  isOpen,
  onClose,
  settings,
  selectWallpaper,
  setCustomWallpaper,
  updateSetting,
  resetSettings,
  quadrants,
  onUpdateQuadrant,
  user,
  fileHandle,
  isFileSystemSupported,
  isLegacyFileInputSupported,
  loadData,
  loadDataLegacy,
  saveData,
  loginWithGoogle,
  logout,
}) {
  const [activeTab, setActiveTab] = useState('wallpaper');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="settings-backdrop" data-testid="settings-backdrop" onClick={onClose}>
        <motion.div
          className="settings-modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
        >
          {/* Header */}
          <div className="analytics-header">
            <div className="analytics-title-group">
              <Settings size={20} className="text-blue-500" />
              <h2>Cài Đặt Ứng Dụng</h2>
            </div>
            <button className="analytics-close-btn" onClick={onClose} aria-label="Đóng cài đặt">
              <X size={18} />
            </button>
          </div>

          {/* Settings Tabs */}
          <div className="analytics-tabs settings-tab-bar">
            <button
              className={`analytics-tab-btn ${activeTab === 'wallpaper' ? 'active' : ''}`}
              onClick={() => setActiveTab('wallpaper')}
            >
              <Palette size={15} /> Hình Nền
            </button>
            <button
              className={`analytics-tab-btn ${activeTab === 'quadrants' ? 'active' : ''}`}
              onClick={() => setActiveTab('quadrants')}
            >
              <LayoutGrid size={15} /> Góc Phần Tư
            </button>
            <button
              className={`analytics-tab-btn ${activeTab === 'backup' ? 'active' : ''}`}
              onClick={() => setActiveTab('backup')}
            >
              <Database size={15} /> Dữ Liệu & Sao Lưu
            </button>
            <button
              className={`analytics-tab-btn ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              <Sliders size={15} /> Tùy Chọn Chung
            </button>
          </div>

          {/* Modal Body */}
          <div className="settings-body">
            {activeTab === 'wallpaper' && (
              <WallpaperPicker
                settings={settings}
                selectWallpaper={selectWallpaper}
                setCustomWallpaper={setCustomWallpaper}
                updateSetting={updateSetting}
              />
            )}

            {activeTab === 'quadrants' && (
              <QuadrantSettings
                quadrants={quadrants}
                onUpdateQuadrant={onUpdateQuadrant}
              />
            )}

            {activeTab === 'backup' && (
              <BackupSettings
                user={user}
                fileHandle={fileHandle}
                isFileSystemSupported={isFileSystemSupported}
                isLegacyFileInputSupported={isLegacyFileInputSupported}
                loadData={loadData}
                loadDataLegacy={loadDataLegacy}
                saveData={saveData}
                loginWithGoogle={loginWithGoogle}
                logout={logout}
              />
            )}

            {activeTab === 'general' && (
              <GeneralSettings
                settings={settings}
                updateSetting={updateSetting}
                resetSettings={resetSettings}
              />
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
